import { 
	Component, 
	Element, 
	Host, 
	Prop, 
	State, 
	h, 
	Event, 
	EventEmitter,
	Method
} from "@stencil/core";

@Component({
	tag: "smart-select",
	styleUrl: "smart-select.scss",
	shadow: true,
})
export class SmartSelect {
	@Prop() name?: string;
	@Prop() label?: string;
	@Prop() disabled: boolean;
	@Prop() defaultValue?: string;
	@Prop() placeholder?: string;
	@Prop() message?: string;
	@Prop({mutable: true}) isOpen = false;

	@State() selectedText = this.placeholder || "Select";
	@State() value: string | null = null;
	// eslint-disable-next-line @stencil-community/strict-mutable
	@Prop({ mutable: true }) error = false;

	@Event() smartChange: EventEmitter<string>;

	@Element() element: HTMLSmartSelectElement;
	private select: HTMLButtonElement | null = null;
	private hiddenInput: HTMLInputElement;

	@Method()
	async focusInner() {
		if(this.select) {
			this.select.focus();
		} else {
			this.isOpen = true;
		}
	}

	private selectOption(value: string, text: string) {
		if (!this.disabled) {
			this.selectedText = text;
			this.value = value;
			this.isOpen = false;

			if (this.select) {
				this.select.focus();
			}

			if(this.hiddenInput) {
				this.hiddenInput.value = value;
			}

			this.smartChange.emit(value);
		}

		this.focusOut();
	}

	private showOptions() {
		this.isOpen = !this.isOpen;
	}

	private createHiddenInput() {
		this.hiddenInput = document.createElement("input");
		this.hiddenInput.type = "text";
		this.hiddenInput.name = this.name;
		this.hiddenInput.value = this.value || "";
		this.hiddenInput.style.visibility = "hidden";
		this.hiddenInput.style.position = "absolute";
		this.hiddenInput.style.pointerEvents = "none";
		this.element.appendChild(this.hiddenInput);
	}

	componentWillLoad() {
		if(this.defaultValue) {
			const options = this.element.querySelectorAll("option");
			let defaultValueOption = null;

			options.forEach(opt => {
				if(opt.value === this.defaultValue) {
					defaultValueOption = opt as HTMLOptionElement;
				}
			});
			this.selectedText = defaultValueOption.value;
			this.value = defaultValueOption.textContent;
		}

		if (this.name) {
			this.createHiddenInput();
		}
	}

	private focusOut(needDelay = false) {
		if(needDelay) {
			setTimeout(() => {
				this.isOpen = false;
			}, 300);
		} else {
			this.isOpen = false;
		}
	}

	private renderOptions() {
		return (
			<ul 
				part="select-options"
				class={{
					"dropdown-options": true,
					"dropdown-options--label": Boolean(this.label)
				}}>
				{this.element.children.length > 0 &&
					Array.from(this.element.children).map((child: HTMLElement) => {
						if (child.tagName === "OPTION") {
							return (
								<li>
									<button
										onClick={(event: MouseEvent) => {
											event.stopPropagation();
											this.selectOption(child.getAttribute("value"), child.textContent);
										}}
									>
										{child.textContent}
									</button>
								</li>
							);
						}
					})}
			</ul>
		);
	}

	render() {
		const randomId: string = (this.name ? this.name : "sid") + new Date().getTime();

		return (
			<Host
				onBlur={() => this.focusOut(true)}
				part="select-element"
			>
				{this.label && (
					<label
						id={`label-${randomId}`}
						htmlFor={randomId}
						class={{ disabled: this.disabled, error: this.error }}
					>
						<smart-text as="span" variant="subtitle-1">
							{this.label}
						</smart-text>
					</label>
				)}
				<button
					tabIndex={1}
					onClick={() => this.showOptions()}
					ref={el => (this.select = el)}
					class={{"select": true, error: this.error}}
					id={randomId}
					disabled={this.disabled}
				>
					{this.selectedText}
					<smart-icon color={this.disabled ? "#9F9FA7" : "#000"} weight="bold" phosphor-icon="ph-caret-down" size={24}></smart-icon>
				</button>
				{this.message && <span 
					part="message-element" class={{ disabled: this.disabled, error: this.error }}>{this.message}</span>}
				{this.isOpen && this.renderOptions()}
			</Host>
		);
	}
}
